import type { ComponentType, FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Loading } from './ui.js';

/**
 * A page that is downloaded the first time it is needed, so the app's first download carries only the pages most people
 * open (My work, Projects, Team). Until it is there the page shows the usual loading skeleton. `preload` starts the
 * download early (the app does it for every lazy page once the first page is up, so a later click does not wait).
 */
export type LazyPage<P> = FunctionComponent<P> & { preload: () => Promise<unknown> };

export function lazyPage<P extends object>(load: () => Promise<ComponentType<P>>): LazyPage<P> {
  let C: ComponentType<P> | null = null;
  let pending: Promise<unknown> | null = null;
  const preload = () =>
    (pending ??= load().then(
      (c) => {
        C = c;
      },
      (e: unknown) => {
        // a failed download (offline, or a new deploy removed the old file) is tried again next time
        pending = null;
        throw e;
      }
    ));
  const Page = (props: P) => {
    const [, ready] = useState(0);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
      if (C) return;
      let live = true;
      preload().then(
        () => live && ready((n) => n + 1),
        () => live && setFailed(true)
      );
      return () => {
        live = false;
      };
    }, []);
    if (C) return <C {...props} />;
    // a deploy since this tab opened: the old page file is gone, so load the new app
    if (failed) {
      if (reloadOnce()) return null;
      return <Loading />;
    }
    return <Loading />;
  };
  return Object.assign(Page, { preload });
}

/** All lazy pages, downloaded in the background once the first page is up. */
export function preloadAll(pages: LazyPage<never>[]) {
  const go = () => pages.forEach((p) => void p.preload().catch(() => undefined));
  if (typeof requestIdleCallback === 'function') requestIdleCallback(go, { timeout: 3000 });
  else setTimeout(go, 1500);
}

/** One reload per tab to pick up a new deploy; never a loop (offline keeps showing the skeleton). */
function reloadOnce(): boolean {
  try {
    if (sessionStorage.getItem(RELOADED)) return false;
    sessionStorage.setItem(RELOADED, '1');
  } catch {
    return false;
  }
  location.reload();
  return true;
}
const RELOADED = 'kff.lazy.reloaded';
